<script setup lang="ts">
import { NavbarDropdownTabExercise, NavbarDropdownTabRelax } from '#components';
import { autoUpdate, useFloating } from '@floating-ui/vue';
import { match } from 'ts-pattern';
import type { Component } from 'vue';
import type { NavbarDropdownType } from '.';

const { reference, open, type } = defineProps<{
  reference: HTMLElement | null;
  open: boolean;
  type: NavbarDropdownType | null;
}>();
defineEmits<{ close: [] }>();
defineSlots<{ default: () => void }>();

const dropdown = useTemplateRef('dropdown_ref');
const { floatingStyles } = useFloating(
  computed(() => reference),
  dropdown,
  {
    open: computed(() => open),
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
  },
);
const { height: referenceHeight } = useElementSize(computed(() => reference));

const content = useTemplateRef('content_ref');
const { height: contentHeight } = useElementSize(content, undefined, {
  box: 'border-box',
});

const tab = computed(() => {
  return match(type)
    .returnType<Component | null>()
    .with('relax', () => NavbarDropdownTabRelax as Component)
    .with('exercise', () => NavbarDropdownTabExercise as Component)
    .with(null, () => null)
    .exhaustive();
});
</script>

<template>
  <div
    ref="dropdown_ref"
    :style="{ ...floatingStyles, height: `calc(100% - ${referenceHeight}px)` }"
    class="pointer-events-none relative isolate size-full *:pointer-events-auto"
  >
    <Transition
      enter-from-class="backdrop-blur-none"
      leave-to-class="backdrop-blur-none"
    >
      <div
        v-if="open"
        class="bg-client-black/10 absolute inset-0 -z-10 size-full backdrop-blur-lg transition duration-250 ease-in-out"
        @mouseenter="$emit('close')"
      ></div>
    </Transition>
    <Transition enter-from-class="max-h-0" leave-to-class="max-h-0">
      <div
        v-show="open"
        :style="`--content-height: ${contentHeight}px`"
        class="bg-client-black h-screen max-h-(--content-height) w-full overflow-hidden transition-[max-height,height] duration-250 ease-in-out"
      >
        <PageContainerAlignment ref="content_ref" class="py-r5 overflow-x-clip">
          <NavbarDropdownTabTransition :dropdown-type="type">
            <component :is="tab" />
          </NavbarDropdownTabTransition>
        </PageContainerAlignment>
      </div>
    </Transition>
  </div>
</template>
